// The energy economy — the "physics" of the world that selection acts on.
//
// These are deliberately NOT gene-controlled: they define the costs and payoffs
// every animal faces, and evolution finds strategies within them. They live in a
// mutable object (rather than module consts) so a headless sweep can vary the
// whole ecology and measure which economies produce a persistent system. A run
// is still fully deterministic for a given (ECON, seed, params).
//
// Guiding principle: every trait must be TWO-SIDED. A gene that is pure benefit
// (or pure cost) doesn't evolve — it slides to whichever end of its range is
// free and pins there, which tells you nothing. Sense costs energy; size buys
// intake but burns more; speed trades against the ability to graze.

export interface Econ {
  /** Energy/s burned at rest, at metabolicRate 1 and size 1. */
  idleCost: number;
  /** Energy/s burned at full speed, at size 1 (scales with moveFrac²). */
  moveCost: number;

  // --- Expensive tissue -----------------------------------------------------
  // Neural tissue is metabolically extravagant: in humans the brain is ~2% of
  // body mass and ~20% of resting metabolism. Modelling that is what keeps
  // perception honest — a wide sense radius is not taxed by fiat, it is
  // expensive because the brain burning through that input stream is expensive.
  // An animal can genuinely trade a dimmer brain for wider senses, or the
  // reverse, and both are viable strategies.

  /** Energy/s to run a full-mass brain (all hidden units at gain 1). */
  brainCost: number;
  /** Energy/s for the sensory apparatus itself at max radius, brain aside. */
  senseCost: number;
  /**
   * Energy/s to PROCESS a max-radius sensory stream with a full-mass brain.
   * Wider senses mean more to think about, so this term is the product of the
   * two — which is what makes "cheap brain + keen senses" a real, if dim,
   * strategy rather than a free lunch.
   */
  senseLoad: number;

  /**
   * Reference speed in m/s for locomotion cost — the speed at which a size-1
   * animal burns `moveCost`.
   *
   * Locomotion is priced on ABSOLUTE velocity, not on a fraction of the
   * animal's own maximum. Pricing it on the fraction made `maxSpeed` a free
   * gene: a faster animal covered more ground for identical energy at the same
   * fraction of its top speed, and could graze while doing it, so the gene was
   * pure benefit and railed to its ceiling. Physics does not know what an
   * animal's personal maximum is.
   */
  refSpeed: number;
  /**
   * Energy/s to maintain sprint musculature at the top of the gene's range, at
   * size 1 — billed whether or not the animal actually sprints.
   *
   * The other half of making speed two-sided. Absolute locomotion pricing stops
   * a high top speed being free to USE, but having it would still be free to
   * OWN, and an unused option that costs nothing rails upward just the same.
   * Fast-twitch muscle is expensive tissue that has to be carried and fed at
   * rest, which is why not every prey animal is a sprinter.
   */
  speedCost: number;

  /**
   * Energy/s to build and haul a full-range reserve capacity, at size 1.
   *
   * Storage is not free. Adipose tissue is cheap to maintain per kilogram —
   * that is what makes it good storage — but it is still tissue that has to be
   * grown and then carried everywhere, which is why animals do not evolve
   * unlimited tanks. Until this existed `maxEnergy` appeared nowhere in the
   * cost formula at all, so a bigger tank was pure benefit and pinned to its
   * ceiling on nearly every run of the sweep.
   *
   * Billed linearly on the normalized gene (not squared like senseCost and
   * speedCost) because fat really is roughly proportional in cost, and scaled
   * by kleiber since a larger animal's reserves are larger in absolute terms.
   */
  reserveCost: number;
  /**
   * Energy/s of somatic maintenance at the top of the lifespan range, at size 1.
   *
   * The disposable-soma theory of ageing: an animal only lives a long time if it
   * pays continuously to repair itself, and that budget competes directly with
   * reproduction. Without it `lifespan` is pure benefit — more years means more
   * litters at no charge — and for a species with no predators, nothing else
   * pushes back at all. The wolves duly pinned `lifespan↑` on run after run.
   */
  somaticCost: number;
  /**
   * Energy/s to carry full-range neuromuscular power, at size 1.
   *
   * `maxForce` reads into the hunt roll as `huntAgilityBonus` but appeared in no
   * cost term at all, so agility was free to OWN — the same trap `speedCost`
   * exists to close. Its only counterweight was indirect (a more agile animal
   * reaches high speed sooner, so pays more `moveCost`) and that saturates,
   * because `moveFrac` is capped by maxSpeed rather than by maxForce.
   */
  forceCost: number;
  /**
   * Energy/s of growth overhead for an animal maturing at `refMaturity`, at
   * size 1 — charged only while juvenile, and scaled INVERSELY by maturityAge.
   *
   * Building an adult body in half the time takes roughly twice the power; this
   * is the fast-slow life-history continuum, and it is why precocial growth is
   * expensive. Without it `maturityAge` is pure cost for any species that lacks
   * cooperative breeding and food sharing — for a deer its only two benefits
   * (a juvenile's double share at a carcass, and exemption from breeding-rank
   * contests) are both gated behind features deer do not have, so it bought
   * exactly nothing and could only rail to its floor.
   */
  growthCost: number;
  /** Reference maturation time in sim-seconds, for normalizing growth overhead. */
  refMaturity: number;

  /** Energy/s a size-1 herbivore pulls from grass while stationary. */
  grazeRate: number;
  /**
   * Absolute speed in m/s below which grazing is unimpeded. A ruminant ambles
   * with its head down and crops as it goes — it is the RUN that stops feeding,
   * not motion as such. Setting this to 0 would mean an animal has to come to a
   * dead stop to get anything, which starves the founding generation before it
   * can evolve the patience to stand still.
   *
   * Absolute, for the same reason as `refSpeed`: as a fraction of the animal's
   * own maximum, a faster animal got a proportionally wider window in which it
   * could both travel and eat, which is not a thing speed buys you.
   */
  grazeWalkSpeed: number;
  /**
   * How sharply intake falls off above a walk. At 1.0 a flat-out sprint yields
   * nothing. This is what makes speed genuinely expensive: above a walk you can
   * outrun a wolf or you can eat, not both.
   */
  grazeMovePenalty: number;

  // --- Predation ------------------------------------------------------------
  // Calibrated against field data: 80–90% of wolf attacks on wild ungulates
  // fail, ~20% succeed on white-tailed deer, 14% mean across 14 studies. The
  // base is what a wolf manages against a healthy prime adult; the bonuses are
  // what it manages against one that is old, starving, or small.

  /** Catch probability against a prime, well-fed, evenly-matched animal. */
  huntBaseOdds: number;
  /** Added odds at maximum frailty (senescent or starving prey). */
  huntFrailtyBonus: number;
  /** Added odds as packmates pile in, with diminishing returns. */
  huntPackBonus: number;
  /** How close a packmate must be to count toward the hunt, in metres. */
  packHuntRadius: number;
  /** Added odds swing across the full size mismatch range, ±. */
  huntSizeBonus: number;
  /**
   * Added odds per unit of (predator - prey) top speed, measured in refSpeeds, ±.
   * Small on purpose: a wolf is barely faster than a deer and wins on condition,
   * not on legs. But it must not be ZERO, or legs are a pure cost to both.
   */
  huntSpeedBonus: number;
  /** The same for the agility (maxForce) gap, measured in refForce units, ±. */
  huntAgilityBonus: number;
  /** Reference steering force, for normalizing the agility gap. */
  refForce: number;

  /**
   * Odds SUBTRACTED from an attack on a prey animal signalling at full strength.
   *
   * This is pursuit deterrence, and it is what makes an alarm gene individually
   * selected rather than altruistic. A signalling animal is not being generous
   * to the herd — it is telling the predator "I have seen you", and predators
   * demonstrably break off pursuits against prey that have clearly spotted them.
   * Stotting in Thomson's gazelles is the textbook case.
   *
   * Without this, signalling only ever helps NEIGHBOURS, which selection cannot
   * reward without kin tracking the model does not have — so it would decay to
   * zero everywhere and the gene would be decorative.
   */
  huntDeterrence: number;

  /**
   * Energy/s to maintain full-strength signalling apparatus, at size 1 —
   * billed at rest, like every other capability.
   *
   * Without it the gene is two-sided for PREY and decorative for PREDATORS:
   * conspicuousness only costs you if something hunts you, and nothing hunts an
   * adult wolf, so signalling was invisible to selection and simply drifted.
   * Measured: both species sat near 0.55, barely off the random 0.5 start.
   * A capability must cost something to OWN or it cannot evolve away.
   */
  signalCost: number;

  /**
   * How much a full-strength signaller extends the range at which a PREDATOR
   * can pick it out, as a fraction of the predator's own sense radius.
   *
   * The cost side. A conspicuous animal is safer once seen and seen more often;
   * that tension is the whole gene. Being loud must not be free.
   */
  conspicuousness: number;

  /** Energy content of a kilogram of prey carcass. */
  energyPerKg: number;
  /** Fraction of that a predator actually absorbs before the rest is lost. */
  preyEfficiency: number;
  /** px within which a carnivore catches prey. */
  killDist: number;
  /** Seconds a carnivore must digest before it can kill again (Holling type-II). */
  killHandling: number;

  /**
   * Metres within which a detected predator suppresses feeding. Vigilance is
   * graded over this distance rather than triggered by mere detection: a grazer
   * lifts its head for a wolf at 50 m and keeps eating through one at 400 m.
   *
   * Set this larger than any sense radius and it degenerates to the old binary
   * behaviour, in which seeing a predator at all stopped an animal feeding —
   * which made `senseRadius` a liability and railed it to the floor whenever
   * predators were common. Useful as an A/B control in the sweep.
   */
  alarmDistance: number;
  /**
   * Flight initiation distance in metres — how close a predator must get before
   * the hard-wired "run" reflex fires. Beyond it the brain decides for itself.
   *
   * A real prey animal has a measurable FID and does not bolt at every predator
   * it can perceive; a wolf 400 m away is information, not an emergency. Firing
   * the reflex on mere detection punished keen senses savagely — the further an
   * animal could see, the more of its life it spent running — which is half of
   * why `senseRadius` railed to its floor.
   */
  flightDistance: number;

  /**
   * Maintenance cost of a BEDDED animal, as a fraction of its standing cost.
   *
   * Resting metabolic rate is a fraction of active metabolic rate in every
   * mammal measured, and that gap is the entire reason lying up is worth doing.
   * Without it, stopping saved only locomotion, resting was strictly worse than
   * grazing, and nothing in the world ever lay down.
   */
  restDiscount: number;

  // --- Rumination -----------------------------------------------------------
  // A grazer cannot convert grass to energy as fast as it can crop it. Rumen
  // capacity caps a meal, and the animal has to lie up and process it before it
  // can usefully eat again — which is WHY deer bed down, and why they spend
  // roughly half their lives doing it.
  //
  // Without this, resting was a pure drain: grazing had no digestive limit, so
  // lying down earned nothing while still paying upkeep, and no animal would
  // ever rationally do it. The behavioural bias made them rest anyway, which
  // quietly taxed the whole species. With it, the graze-then-bed rhythm falls
  // out of physiology instead of being imposed by the scaffold.

  /** Gut capacity in energy units per unit of kleiber mass. */
  gutCapacity: number;
  /** Energy/s moved from gut to body per unit of kleiber mass, while active. */
  digestRate: number;
  /** Multiplier on digestion while bedded down — this is what rest is FOR. */
  ruminationBonus: number;

  /**
   * Seconds an animal keeps reacting to an alarm raised by one of its own.
   *
   * Long enough to clear the area, short enough that a herd settles again
   * rather than staying permanently spooked — real vigilance decays over a
   * minute or two after the threat passes.
   */
  alarmMemory: number;

  /**
   * Positional error in a relayed alarm, as a fraction of how far the signal
   * travelled. 0 = an alarm is as good as seeing the predator yourself.
   *
   * It must NOT be 0. A perfect relay makes personal senses redundant, and
   * selection duly discarded them: with an exact position broadcast for twelve
   * seconds, deer `senseRadius` collapsed from 0.40 to 0.058 — straight through
   * the rail. Real alarm says "something is wrong, over there", and you still
   * need your own eyes to act on it.
   */
  alarmVagueness: number;

  /** Seconds before an animal can breed again. */
  breedCooldown: number;
}

export const DEFAULT_ECON: Econ = {
  idleCost: 0.7, // lowered from 1.0 when the capability-tissue costs below were
  // added: they moved load that this flat term had been carrying implicitly into
  // gene-dependent terms, and leaving it at 1.0 double-charged every animal
  moveCost: 3.2,

  brainCost: 0.7,
  senseCost: 0.5,
  senseLoad: 0.9,

  refSpeed: 15, // m/s — near the middle of both species' sprint ranges, chosen so
  // the switch from fractional to absolute pricing left a mid-sized deer's bill
  // almost unchanged and only re-priced the extremes
  speedCost: 0.6, // TUNED — no field source; the muscle analogue of senseCost
  reserveCost: 0.5, // TUNED — no field source; the fat analogue of the same
  somaticCost: 0.5, // TUNED — the price of not ageing (disposable soma)
  forceCost: 0.4, // TUNED — neuromuscular power, the agility analogue of speedCost
  growthCost: 0.8, // TUNED — overhead of building a body, juveniles only
  refMaturity: 120, // sim-seconds; near the middle of both species' maturity ranges

  grazeRate: 13,
  grazeWalkSpeed: 6, // m/s. COMPRESSED: a real deer crops at well under 1 m/s, but
  // the sim's grazing window has to be wide enough that a herd can feed while it
  // drifts. Set tighter than the animals' natural cruising speed this silently
  // switches grazing off altogether, which starved the deer beside a map that was
  // still at 100% grass.
  grazeMovePenalty: 1.0,

  huntBaseOdds: 0.2, // ~20% on white-tailed deer specifically (Minnesota); the 14%
  // figure often quoted is a cross-prey mean pulled down by moose at 6%
  huntFrailtyBonus: 0.45, // → ~59% against a senescent or starving deer
  huntPackBonus: 0.35, // 2 packmates ≈ +20 points, 5 ≈ +27
  packHuntRadius: 90,
  huntSizeBonus: 0.12,
  huntSpeedBonus: 0.30, // TUNED — the wolf/deer sprint gap spans about -0.15 to
  // +0.59 refSpeeds, so this is worth roughly -4 to +18 points of odds
  huntAgilityBonus: 0.15, // TUNED
  refForce: 200,
  signalCost: 0.35, // TUNED — the alarm analogue of senseCost and speedCost
  huntDeterrence: 0.18, // TUNED — a full signal roughly halves the base catch odds
  conspicuousness: 0.5, // TUNED — a full signal is spotted 50% further away // mid-range of both species' maxForce, for normalizing the gap

  energyPerKg: 20,
  preyEfficiency: 0.2, // MEASURED, and biologically the right order: the classic
  // figure for energy transfer between trophic levels is 10-20%, not 50%. Swept
  // 0.50/0.30/0.20/0.12 on the default map, 6 seeds, 60k ticks — 0.20 improved
  // the predator:prey ratio from 1:5 to 1:10 AND lowered population CV from
  // 0.27/0.39 to 0.23/0.28, with persistence unchanged at 6/6. More realism and
  // more stability at once, which is rare enough in this model to note.
  killDist: 10,
  killHandling: 7,

  alarmMemory: 12,
  alarmVagueness: 0.45, // a far-off alarm is a vague one; your own senses still matter // TUNED — long enough to run, short enough to settle again
  alarmDistance: 120, // TUNED — roughly a wolf's charge range, not a field figure
  flightDistance: 200, // TUNED — reported FIDs for deer facing a coursing predator
  // run to a few hundred metres; the ordering (flight > alarm) is the real part

  restDiscount: 0.55, // RMR is roughly half to two-thirds of the standing rate

  gutCapacity: 40, // × kleiber — a couple of seconds of hard grazing fills it
  digestRate: 8.0, // × kleiber — MEASURED, not guessed. This is the ceiling on a
  // grazer's income, so it must clear upkeep (~4.2/s for a mid deer) with enough
  // margin to raise young. At 3.6 the margin was too thin and deer fell to 46 on
  // the first run; swept across 4.5/6/8/11, 8.0 gave 5/6 closed-system seeds with
  // no railed genes on either species.
  ruminationBonus: 2.0, // bedded and chewing cud, a ruminant processes twice as fast

  breedCooldown: 90,// ~1 real year: deer and wolves both breed annually
};

/** The live economy. Mutate via setEcon(); World reads this every tick. */
export const ECON: Econ = { ...DEFAULT_ECON };

/** Overwrite the live economy (used by the headless tuning sweep). */
export function setEcon(patch: Partial<Econ>): void {
  Object.assign(ECON, patch);
}

/** Restore the shipped economy. */
export function resetEcon(): void {
  Object.assign(ECON, DEFAULT_ECON);
}
